import React, { useState } from 'react';
import { StarIcon, ClockIcon, MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/solid';

const ClinicInfoCard = ({ clinicData }) => {
    const [selectedService, setSelectedService] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    if (!clinicData) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading clinic details...</div>
            </div>
        );
    }

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        return timeString.substring(0, 5); // Remove seconds
    };

    const formatDays = (daysString) => {
        if (!daysString) return 'N/A';
        return daysString.split(',').join(', ');
    };

    const openGoogleMaps = () => {
        if (clinicData.googleMapUrl) {
            window.open(clinicData.googleMapUrl, '_blank');
        }
    };

    // Debug logging
    console.log('ClinicInfoCard - averageRating:', clinicData.averageRating, 'Type:', typeof clinicData.averageRating);

    return (
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
                <div className="flex justify-between items-start">
                    <div className="flex-1">
                        <h1 className="text-3xl font-bold mb-2">{clinicData.name}</h1>
                        <p className="text-blue-100 text-lg mb-3">{clinicData.description}</p>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                                <StarIcon className="w-5 h-5 text-yellow-300 mr-1" />
                                <span className="font-semibold">
                                    {parseFloat(clinicData.averageRating) > 0
                                        ? parseFloat(clinicData.averageRating).toFixed(1)
                                        : 'New'
                                    }
                                </span>
                                {parseFloat(clinicData.averageRating) > 0 && (
                                    <span className="text-blue-100 ml-1">/ 5.0</span>
                                )}
                            </div>
                            <div className="flex items-center">
                                <ClockIcon className="w-5 h-5 mr-1" />
                                <span>{formatTime(clinicData.clinicOpenTime)} - {formatTime(clinicData.clinicCloseTime)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="bg-white bg-opacity-20 rounded-lg p-3">
                            <div className="text-2xl font-bold">{clinicData.totalExperience}</div>
                            <div className="text-sm text-blue-100">Years Experience</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Location Section */}
            <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <MapPinIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Location & Contact
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-gray-700 mb-2">{clinicData.address}</p>
                        <p className="text-gray-600 mb-2">
                            {clinicData.area}, {clinicData.city}, {clinicData.state} - {clinicData.pincode}
                        </p>
                        {clinicData.googleMapUrl && (
                            <button
                                onClick={openGoogleMaps}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <MapPinIcon className="w-4 h-4 mr-2" />
                                View on Google Maps
                            </button>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center mb-2">
                            <ClockIcon className="w-5 h-5 text-gray-500 mr-2" />
                            <span className="text-gray-700">
                                <strong>Open:</strong> {formatDays(clinicData.daysClinicsOpen)}
                            </span>
                        </div>
                        <div className="flex items-center mb-2">
                            <ClockIcon className="w-5 h-5 text-gray-500 mr-2" />
                            <span className="text-gray-700">
                                <strong>Hours:</strong> {formatTime(clinicData.clinicOpenTime)} - {formatTime(clinicData.clinicCloseTime)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Services Section */}
            {clinicData.services && clinicData.services.length > 0 && (
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Services Offered</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {clinicData.services.map((service) => (
                            <div
                                key={service.id}
                                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                                    selectedService === service.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-blue-300'
                                }`}
                                onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-semibold text-gray-800">{service.serviceName}</h3>
                                    <span className="text-green-600 font-semibold">₹{service.basePrice}</span>
                                </div>
                                <div className="text-sm text-gray-600 mb-2">
                                    Duration: {service.durationMinutes} minutes
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className={`px-2 py-1 rounded-full text-xs ${
                                        service.isEnabled 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {service.isEnabled ? 'Available' : 'Unavailable'}
                                    </span>
                                    {service.hasPackages && (
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                            Packages Available
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Before/After Images Section */}
            {clinicData.services && clinicData.services.some(service => 
                service.beforeAfterImages && service.beforeAfterImages.length > 0
            ) && (
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Before & After Gallery</h2>
                    <div className="space-y-6">
                        {clinicData.services.map((service) => (
                            service.beforeAfterImages && service.beforeAfterImages.length > 0 && (
                                <div key={service.id} className="border rounded-lg p-4">
                                    <h3 className="font-semibold text-gray-800 mb-3">{service.serviceName}</h3>
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {service.beforeAfterImages.map((image, index) => (
                                            <div key={image.id} className="border rounded-lg overflow-hidden">
                                                <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                                                    <div className="grid grid-cols-2">
                                                        <div className="relative">
                                                            <img
                                                                src={image.beforeImageUrl}
                                                                alt="Before"
                                                                className="w-full h-32 object-cover"
                                                            />
                                                            <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                                                Before
                                                            </div>
                                                        </div>
                                                        <div className="relative">
                                                            <img
                                                                src={image.afterImageUrl}
                                                                alt="After"
                                                                className="w-full h-32 object-cover"
                                                            />
                                                            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                                                After
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {image.description && (
                                                    <div className="p-3">
                                                        <p className="text-sm text-gray-600">{image.description}</p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            {new Date(image.createdAt).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}

            {/* No Services Message */}
            {(!clinicData.services || clinicData.services.length === 0) && (
                <div className="p-6 text-center">
                    <div className="text-gray-500">
                        <p className="text-lg">No services available at the moment.</p>
                        <p className="text-sm mt-2">Please check back later for available services.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClinicInfoCard; 